import { useState } from 'react';
import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useLifecycle, type LifecycleAction } from '@/api/projects';

/** project-lifecycle.md §2: which envelope moves are legal from each state. */
export const LEGAL_ACTIONS: Record<string, LifecycleAction[]> = {
  active: ['complete', 'archive'],
  completed: ['reopen', 'archive'],
  archived: ['restore'],
};
const ALL: LifecycleAction[] = ['complete', 'reopen', 'archive', 'restore'];

export interface LifecycleButtonsProps {
  projectId: string;
  state: string;
}

/** Staff envelope controls; every move asks for a reason that lands in the audit row. */
export function LifecycleButtons({ projectId, state }: LifecycleButtonsProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const lifecycle = useLifecycle(projectId);
  const [pending, setPending] = useState<LifecycleAction | null>(null);
  const [reason, setReason] = useState('');
  const legal = LEGAL_ACTIONS[state] ?? [];

  const confirm = async () => {
    if (!pending) return;
    try {
      await lifecycle.mutateAsync({ action: pending, reason });
      setPending(null);
      setReason('');
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Group gap="xs">
      {ALL.map((a) => (
        <Button key={a} variant={a === 'archive' ? 'light' : 'default'} color={a === 'archive' ? 'red' : undefined} size="xs" disabled={!legal.includes(a)} onClick={() => setPending(a)}>
          {t(`staff.lifecycle.${a}`)}
        </Button>
      ))}
      <Modal opened={!!pending} onClose={() => setPending(null)} title={pending ? t(`staff.lifecycle.${pending}`) : ''}>
        <Stack gap="md">
          <Text fz={14}>{t('staff.lifecycle.reasonHint')}</Text>
          <Textarea label={t('staff.lifecycle.reason')} value={reason} onChange={(e) => setReason(e.currentTarget.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPending(null)}>
              {tc('common.cancel')}
            </Button>
            <Button loading={lifecycle.isPending} onClick={() => void confirm()}>
              {pending ? t(`staff.lifecycle.${pending}`) : ''}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Group>
  );
}
